import React from "react";

import Button from "../../components/primitives/button";
import { ConfirmActionDialog } from "../../components/dialogs";
import "./settings.css";
import {
  detectGlobalPrivacyControl,
  formatSettingsDateTime,
  useSettingsData,
} from "./settings.functions";

export default function Settings() {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const {
    myUserId,
    loading,
    refreshing,
    blockedUsers,
    hiddenConversations,
    error,
    unblockTargetId,
    restoreConversationTargetId,
    deleteAccountPending,
    emailNotificationsEnabled,
    notificationPrefSaving,
    sociopoliticalPublic,
    sociopoliticalVisibilitySaving,
    sensitiveAttributesPublic,
    sensitiveVisibilitySaving,
    blockedCountLabel,
    hiddenConversationCountLabel,
    disableRefresh,
    loadSettingsData,
    handleUnblock,
    handleUnhideConversation,
    handleToggleEmailNotifications,
    handleToggleSociopoliticalVisibility,
    handleToggleSensitiveAttributesVisibility,
    handleTogglePrivacyOptOut,
    handleDeleteAccount,
    privacyOptOut,
    privacyOptOutSaving,
  } = useSettingsData();
  const gpcEnabled = detectGlobalPrivacyControl();
  return (
    <div className="settingsScreen">
      {deleteAccountPending ? (
        <div
          className="settingsScreen__deletionOverlay"
          role="alertdialog"
          aria-modal="true"
          aria-live="assertive"
          aria-labelledby="settingsScreen__deletionTitle"
          aria-describedby="settingsScreen__deletionMessage"
        >
          <div className="settingsScreen__deletionPanel">
            <div className="settingsScreen__deletionSpinner" aria-hidden="true" />
            <h2
              id="settingsScreen__deletionTitle"
              className="settingsScreen__deletionTitle"
            >
              Starting account deletion
            </h2>
            <p
              id="settingsScreen__deletionMessage"
              className="settingsScreen__deletionMessage"
            >
              Your deletion request is being queued and will continue even after sign-out.
            </p>
          </div>
        </div>
      ) : null}
      <div className="settingsScreen__container">
        <section className="settingsScreen__box">
          {/* Page header */}
          <h1 className="settingsScreen__title">Settings</h1>

          {/* Error state */}
          {error ? (
            <div className="settingsScreen__alert settingsScreen__alert--error">{error}</div>
          ) : null}


          <div className="settingsScreen__content">
            {/* Notifications */}
            <section className="settingsScreen__card">
              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">Notifications</h2>
                  <p className="settingsScreen__cardMeta">
                    Email me when I receive a message or chat request while I'm
                    offline.
                  </p>
                </div>

                <label
                  className="settingsScreen__toggle"
                  htmlFor="settingsScreen__emailNotifications"
                >
                  <span className="settingsScreen__toggleLabel">
                    {emailNotificationsEnabled ? "On" : "Off"}
                  </span>
                  <input
                    id="settingsScreen__emailNotifications"
                    className="settingsScreen__toggleInput"
                    type="checkbox"
                    role="switch"
                    aria-checked={emailNotificationsEnabled}
                    checked={emailNotificationsEnabled}
                    onChange={handleToggleEmailNotifications}
                    disabled={!myUserId || notificationPrefSaving}
                  />
                  <span className="settingsScreen__toggleTrack" aria-hidden="true">
                    <span className="settingsScreen__toggleThumb" />
                  </span>
                </label>
              </div>
            </section>

            {/* Privacy */}
            <section className="settingsScreen__card">
              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">Privacy</h2>
                  <p className="settingsScreen__cardMeta">
                    Show my sociopolitical answers on my profile. When off,
                    other members see "This member's answers are private."
                  </p>
                </div>

                <label
                  className="settingsScreen__toggle"
                  htmlFor="settingsScreen__sociopoliticalVisibility"
                >
                  <span className="settingsScreen__toggleLabel">
                    {sociopoliticalPublic ? "Public" : "Private"}
                  </span>
                  <input
                    id="settingsScreen__sociopoliticalVisibility"
                    className="settingsScreen__toggleInput"
                    type="checkbox"
                    role="switch"
                    aria-checked={sociopoliticalPublic}
                    checked={sociopoliticalPublic}
                    onChange={handleToggleSociopoliticalVisibility}
                    disabled={!myUserId || sociopoliticalVisibilitySaving}
                  />
                  <span className="settingsScreen__toggleTrack" aria-hidden="true">
                    <span className="settingsScreen__toggleThumb" />
                  </span>
                </label>
              </div>

              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">
                    Sensitive details on my profile
                  </h2>
                  <p className="settingsScreen__cardMeta">
                    Show my sexual orientation, religion, ethnicity, and
                    political views on my profile. When off, these stay private
                    and other members can't see them.
                  </p>
                </div>

                <label
                  className="settingsScreen__toggle"
                  htmlFor="settingsScreen__sensitiveVisibility"
                >
                  <span className="settingsScreen__toggleLabel">
                    {sensitiveAttributesPublic ? "Public" : "Private"}
                  </span>
                  <input
                    id="settingsScreen__sensitiveVisibility"
                    className="settingsScreen__toggleInput"
                    type="checkbox"
                    role="switch"
                    aria-checked={sensitiveAttributesPublic}
                    checked={sensitiveAttributesPublic}
                    onChange={handleToggleSensitiveAttributesVisibility}
                    disabled={!myUserId || sensitiveVisibilitySaving}
                  />
                  <span className="settingsScreen__toggleTrack" aria-hidden="true">
                    <span className="settingsScreen__toggleThumb" />
                  </span>
                </label>
              </div>
            </section>

            {/* Your Privacy Choices */}
            <section className="settingsScreen__card">
              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">Your Privacy Choices</h2>
                  <p className="settingsScreen__cardMeta">
                    We do not sell or share your personal information, and we do
                    not run targeted advertising. Your sensitive details (sexual
                    orientation, religion, ethnicity, political views) are kept
                    private by default — use the toggle above to limit or share
                    them. To request access to or deletion of your data, email{" "}
                    <a className="settingsScreen__inlineLink" href="mailto:privacy@betterintentions.com">
                      privacy@betterintentions.com
                    </a>
                    .
                  </p>
                  {gpcEnabled ? (
                    <p className="settingsScreen__cardMeta">
                      We detected a Global Privacy Control signal from your
                      browser and recorded it as a "do not sell or share"
                      opt-out on your account. The signal governs while it is
                      active, so the toggle below stays on.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">
                    Do not sell or share my personal information
                  </h2>
                  <p className="settingsScreen__cardMeta">
                    Records your opt-out preference on your account. We honor it
                    today by never selling or sharing your data, and it will
                    block any such feature we might introduce in the future.
                  </p>
                </div>

                <label
                  className="settingsScreen__toggle"
                  htmlFor="settingsScreen__privacyOptOut"
                >
                  <span className="settingsScreen__toggleLabel">
                    {gpcEnabled || privacyOptOut ? "On" : "Off"}
                  </span>
                  <input
                    id="settingsScreen__privacyOptOut"
                    className="settingsScreen__toggleInput"
                    type="checkbox"
                    role="switch"
                    aria-checked={gpcEnabled || privacyOptOut}
                    checked={gpcEnabled || privacyOptOut}
                    onChange={handleTogglePrivacyOptOut}
                    disabled={!myUserId || privacyOptOutSaving || gpcEnabled}
                  />
                  <span className="settingsScreen__toggleTrack" aria-hidden="true">
                    <span className="settingsScreen__toggleThumb" />
                  </span>
                </label>
              </div>
            </section>

            {/* Blocked users */}
            <section className="settingsScreen__card">
              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">Blocked Users</h2>
                  <p className="settingsScreen__cardMeta">{blockedCountLabel}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="btn--inline"
                  onClick={() => loadSettingsData({ silent: true })}
                  disabled={disableRefresh}
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>
              </div>

              {loading ? (
                <div className="settingsScreen__empty">Loading blocked users...</div>
              ) : blockedUsers.length === 0 ? (
                <div className="settingsScreen__empty">You have not blocked anyone.</div>
              ) : (
                <div className="settingsScreen__list" role="list">
                  {blockedUsers.map((entry) => (
                    <div className="settingsScreen__row" key={entry.cognitoId} role="listitem">
                      <div className="settingsScreen__identity">
                        <div className="settingsScreen__name">
                          {entry.profileName || "Unknown user"}
                        </div>
                        <div className="settingsScreen__details">
                          <span className="settingsScreen__detailLabel">Blocked:</span>{" "}
                          {formatSettingsDateTime(entry.blockedAt)}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="btn--inline"
                        onClick={() => handleUnblock(entry.cognitoId)}
                        disabled={Boolean(unblockTargetId)}
                      >
                        {unblockTargetId === entry.cognitoId ? "Unblocking..." : "Unblock"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Hidden conversations */}
            <section className="settingsScreen__card">
              <div className="settingsScreen__cardHeader">
                <div>
                  <h2 className="settingsScreen__cardTitle">Hidden Conversations</h2>
                  <p className="settingsScreen__cardMeta">{hiddenConversationCountLabel}</p>
                </div>
              </div>

              {!myUserId ? (
                <div className="settingsScreen__empty">Loading your account...</div>
              ) : loading ? (
                <div className="settingsScreen__empty">Loading hidden conversations...</div>
              ) : hiddenConversations.length === 0 ? (
                <div className="settingsScreen__empty">No hidden conversations.</div>
              ) : (
                <div className="settingsScreen__list" role="list">
                  {hiddenConversations.map((entry) => (
                    <div className="settingsScreen__row" key={entry.chatId} role="listitem">
                      <div className="settingsScreen__identity">
                        <div className="settingsScreen__name">{entry.title}</div>
                        <div className="settingsScreen__details">
                          <span className="settingsScreen__detailLabel">Hidden:</span>{" "}
                          {formatSettingsDateTime(entry.hiddenAt)}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="btn--inline"
                        onClick={() => handleUnhideConversation(entry.chatId)}
                        disabled={Boolean(restoreConversationTargetId)}
                      >
                        {restoreConversationTargetId === entry.chatId
                          ? "Restoring..."
                          : "Unhide"}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="settingsScreen__card settingsScreen__card--danger">
              <div className="settingsScreen__cardHeader">
                <h2 className="settingsScreen__cardTitle">Delete Account</h2>
                <Button
                  variant="outline"
                  size="sm"
                  className="btn--inline settingsScreen__dangerButton"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={!myUserId || deleteAccountPending}
                >
                  {deleteAccountPending ? "Deleting..." : "Delete Account"}
                </Button>
              </div>
            </section>
          </div>
        </section>
      </div>

      <ConfirmActionDialog
        isOpen={deleteDialogOpen}
        title="Delete your account?"
        message="This permanently deletes your profile, direct chats, messages, and account access. Deletion continues even after sign-out."
        confirmLabel="Delete Account"
        cancelLabel="Cancel"
        submitting={deleteAccountPending}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          const deleted = await handleDeleteAccount();
          if (deleted) {
            setDeleteDialogOpen(false);
          }
        }}
      />
    </div>
  );
}
